import {
    type CreateExtArgs,
    CreateExtensionArgument,
    type DalValue,
    type Extension,
    type ExtensionAPI,
    pointerUtils
} from '@wix/document-manager-core'
import type {Pointer} from '@wix/document-services-types'
import * as jsonSchemas from '@wix/document-services-json-schemas'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {VariantsExtensionAPI} from '../variants/variants'
import * as constants from '../../constants/constants'
import {displayedOnlyStructureUtil} from '@wix/santa-core-utils'
const {isReferredId} = displayedOnlyStructureUtil
import _ from 'lodash'

export type FeatureData = any

export interface FeaturesAPI {
    component: {
        get(compPointer: Pointer, featureName: string): FeatureData
    }
    site: {
        get(featureName: string): FeatureData
        update(featureName: string, value: DalValue): Pointer
    }
}

export type FeaturesExtensionAPI = ExtensionAPI & {
    features: FeaturesAPI
}

export const FEATURE_TYPES = {
    COMPONENT: 'Component',
    PAGE: 'Page',
    SITE: 'Site',
    REPEATER: 'Repeater'
}

const validPageLevelTypes = ['Page', 'Document']

class RemoteComponentFeatureNotSupportedError extends Error {
    constructor(namespace: string, componentId: string) {
        super(
            `feature ${namespace} is not supported for remote components. You sent a component with id ${componentId}`
        )
        this.name = 'RemoteComponentFeatureNotSupportedError'
    }
}

const {
    namespaceMapping: {featuresData, getNamespaceConfig}
} = jsonSchemas

const FEATURE_CONFIGS_BY_TYPE = _(featuresData)
    .groupBy('featureType')
    .mapValues(group =>
        _(group)
            .keyBy('name')
            .mapValues(({name}) => ({
                name,
                isEditorOnlyFeature: !getNamespaceConfig(name).isPublicViewerData
            }))
            .value()
    )
    .value()

const masterPagePointer = pointerUtils.getPagePointer(constants.MASTER_PAGE_ID, constants.VIEW_MODES.DESKTOP)

const createExtension = ({experimentInstance}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = ({dal, pointers, extensionAPI}: CreateExtArgs): FeaturesExtensionAPI => {
        const {dataModel} = extensionAPI as DataModelExtensionAPI
        const {variants} = extensionAPI as VariantsExtensionAPI

        const validateFeatureType = (compPointer: Pointer, namespace: string, expectedFeatureType: string) => {
            if (!featuresData[namespace]) {
                throw new Error(`feature ${namespace} doesn't exist`)
            }

            const {featureType} = featuresData[namespace]

            if (featureType !== expectedFeatureType) {
                throw new Error(`feature ${namespace} cannot be used through ${expectedFeatureType} feature APIs`)
            }
            if (isReferredId(compPointer.id) && !getNamespaceConfig(namespace).isPublicViewerData) {
                throw new RemoteComponentFeatureNotSupportedError(namespace, compPointer.id)
            }

            if (featureType === FEATURE_TYPES.COMPONENT) {
                // all components can use a Component feature
                return
            }
            const type = dal.get(pointers.getInnerPointer(compPointer, ['type']))

            if (featureType === FEATURE_TYPES.PAGE && !validPageLevelTypes.includes(type)) {
                throw new Error(`feature ${namespace} cannot be used for a non-page component`)
            }

            if (featureType === FEATURE_TYPES.SITE && type !== 'Document') {
                throw new Error(`feature ${namespace} is a site feature, and cannot be used for a page or component`)
            }

            if (featureType === FEATURE_TYPES.REPEATER && type !== 'RepeaterContainer') {
                throw new Error(
                    `feature ${namespace} is a repeater feature, and cannot be used for any other type of component`
                )
            }
        }

        const getNamespaceData = (componentPointer: Pointer, namespace: string) => {
            const config = getNamespaceConfig(namespace)
            if (config.supportsVariants) {
                return variants.getComponentItemConsideringVariants(componentPointer, namespace)
            }
            return dataModel.components.getItem(componentPointer, namespace)
        }

        const updateNamespaceData = (componentPointer: Pointer, namespace: string, value: DalValue) => {
            const config = getNamespaceConfig(namespace)
            if (config.supportsVariants) {
                return variants.updateDataConsideringVariants({
                    pointerWithVariants: componentPointer,
                    valueToUpdate: value,
                    namespace
                })
            }
            return dataModel.components.addItem(componentPointer, namespace, value)
        }

        const getFeatureData = (featureType: string, compPointer: Pointer, featureName: string): FeatureData => {
            try {
                validateFeatureType(compPointer, featureName, featureType)
            } catch (error) {
                if (
                    !experimentInstance.isOpen('dm_throwForRemoteCompsEditorFeatures') &&
                    error instanceof RemoteComponentFeatureNotSupportedError
                ) {
                    return
                }
                throw error
            }
            return getNamespaceData(compPointer, featureName)
        }

        const updateFeatureData = (
            featureType: string,
            compPointer: Pointer,
            featureName: string,
            value: DalValue
        ): Pointer | undefined => {
            try {
                validateFeatureType(compPointer, featureName, featureType)
            } catch (error) {
                if (
                    !experimentInstance.isOpen('dm_throwForRemoteCompsEditorFeatures') &&
                    error instanceof RemoteComponentFeatureNotSupportedError
                ) {
                    return
                }
                throw error
            }
            return updateNamespaceData(compPointer, featureName, value)
        }

        return {
            features: {
                component: {
                    get: (compPointer: Pointer, featureName: string) =>
                        getFeatureData(FEATURE_TYPES.COMPONENT, compPointer, featureName)
                },
                site: {
                    get: (featureName: string) => getFeatureData(FEATURE_TYPES.SITE, masterPagePointer, featureName),
                    update: (featureName: string, value: DalValue) =>
                        updateFeatureData(FEATURE_TYPES.SITE, masterPagePointer, featureName, value)! //this will always return a pointer for site features
                }
            }
        }
    }
    const createPublicAPI = () => {
        return {
            features: {
                components: {
                    FEATURE_TYPES: FEATURE_CONFIGS_BY_TYPE[FEATURE_TYPES.COMPONENT] ?? {}
                },
                repeater: {
                    FEATURE_TYPES: FEATURE_CONFIGS_BY_TYPE[FEATURE_TYPES.REPEATER] ?? {}
                },
                page: {
                    FEATURE_TYPES: FEATURE_CONFIGS_BY_TYPE[FEATURE_TYPES.PAGE] ?? {}
                },
                site: {
                    FEATURE_TYPES: FEATURE_CONFIGS_BY_TYPE[FEATURE_TYPES.SITE] ?? {}
                }
            }
        }
    }

    return {
        name: 'features',
        createExtensionAPI,
        createPublicAPI,
        dependencies: new Set(['dataModel'])
    }
}

export {createExtension}
